import { useState, useEffect } from 'react';
import { Button, Input } from '@agendor/ui';
import { FormGroup, InputGroup, TextArea, HelperText, Checkbox, CheckboxLabel, LabelRow } from '../components/Input';
import { Modal } from '../components/Modal';
import BusinessService, { BusinessDetails } from '../business-service';
import { useSingleModalRoute } from '../navigation';
import { useToast } from '../toast-context';
import { ConfigCard, ConfigInfo, ConfigTitle, ConfigSummary, SummaryItem } from './AssistantSection';

type PersonalizationCardProps = {
    business: BusinessDetails;
    service: BusinessService | null;
    onReloadBusiness: () => Promise<void> | void;
    isActive: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
};

export default function PersonalizationCard({
    business,
    service,
    onReloadBusiness,
    isActive,
    modalSegments,
    onNavigate,
}: PersonalizationCardProps) {
    const [greetingEnabled, setGreetingEnabled] = useState(false);
    const [greetingDraft, setGreetingDraft] = useState('');
    const [personalityDraft, setPersonalityDraft] = useState('');
    const [handoffDraft, setHandoffDraft] = useState(true);
    const [splitMessagesDraft, setSplitMessagesDraft] = useState(false);
    const [saving, setSaving] = useState(false);

    const { showToast } = useToast();

    const { isOpen, open, close } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'personality',
    });

    useEffect(() => {
        if (isOpen) {
            setGreetingEnabled(business.bot.greetingMessage !== null);
            setGreetingDraft(business.bot.greetingMessage ?? '');
            setPersonalityDraft(business.bot.personality);
            setHandoffDraft(business.bot.handoffEnabled);
            setSplitMessagesDraft(business.bot.splitMessagesEnabled ?? false);
        }
    }, [isOpen, business]);

    const handleSave = async () => {
        if (!service) return;

        if (greetingEnabled && !greetingDraft.trim()) {
            showToast('Preencha a mensagem de saudação ou desmarque a opção.', 'error', 'modal');
            return;
        }

        if (!personalityDraft.trim()) {
            showToast('Preencha o tom do assistente.', 'error', 'modal');
            return;
        }

        setSaving(true);
        try {
            const greetingMessage = greetingEnabled ? greetingDraft.trim() : null;
            await service.setPersonalization({
                greetingMessage,
                personality: personalityDraft.trim(),
                handoffEnabled: handoffDraft,
                ...(business.bot.splitMessagesEnabled !== undefined && { splitMessagesEnabled: splitMessagesDraft }),
            });
            await onReloadBusiness();
            showToast('Personalidade salva com sucesso!', 'success', 'app');
            close();
        } catch (error) {
            console.error('Failed to save personalization', error);
            showToast('Não foi possível salvar a personalidade.', 'error', 'modal');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <ConfigCard data-testid="personalization-card" onClick={() => open()} style={{ marginTop: '12px' }}>
                <ConfigInfo>
                    <ConfigTitle>Personalidade</ConfigTitle>
                    <ConfigSummary>
                        <SummaryItem>Tom: {business.bot.personality}</SummaryItem>
                        <SummaryItem>
                            {business.bot.greetingMessage ? 'Saudação personalizada' : 'Saudação automática'}
                        </SummaryItem>
                        {business.bot.splitMessagesEnabled !== undefined && (
                            <SummaryItem>
                                {business.bot.splitMessagesEnabled ? 'Mensagens separadas' : 'Mensagens inteiras'}
                            </SummaryItem>
                        )}
                    </ConfigSummary>
                </ConfigInfo>
            </ConfigCard>

            <Modal
                isOpen={isOpen}
                onClose={close}
                title="Personalidade"
                data-testid="personalization-modal"
                footer={
                    <>
                        <Button onClick={close} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button
                            primary
                            data-testid="personalization-save-button"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <FormGroup>
                    <InputGroup>
                        <LabelRow>Tom do assistente</LabelRow>
                        <Input
                            value={personalityDraft}
                            onChange={(e) => setPersonalityDraft(e.target.value)}
                            maxLength={100}
                        />
                        <HelperText>{personalityDraft.length}/100 caracteres</HelperText>
                    </InputGroup>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <CheckboxLabel>
                            <Checkbox
                                checked={greetingEnabled}
                                onChange={(e) => setGreetingEnabled(e.target.checked)}
                            />
                            Saudação personalizada
                        </CheckboxLabel>
                        {greetingEnabled && (
                            <InputGroup style={{ width: '100%' }}>
                                <TextArea
                                    value={greetingDraft}
                                    onChange={(e) => setGreetingDraft(e.target.value)}
                                    maxLength={300}
                                    rows={4}
                                    placeholder="Ex: Olá! Bem-vindo à Barbearia do João. Como posso ajudar você hoje?"
                                />
                                <HelperText>{greetingDraft.length}/300 caracteres</HelperText>
                            </InputGroup>
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <CheckboxLabel>
                            <Checkbox
                                data-testid="handoff-enabled-checkbox"
                                checked={handoffDraft}
                                onChange={(e) => setHandoffDraft(e.target.checked)}
                            />
                            Aviso de atendimento humano
                        </CheckboxLabel>
                        <HelperText>
                            Você recebe uma mensagem quando o cliente pede para falar com uma pessoa ou quando o
                            assistente não consegue ajudar.
                        </HelperText>
                    </div>

                    {business.bot.splitMessagesEnabled !== undefined && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <CheckboxLabel>
                                <Checkbox
                                    data-testid="split-messages-checkbox"
                                    checked={splitMessagesDraft}
                                    onChange={(e) => setSplitMessagesDraft(e.target.checked)}
                                />
                                Separar mensagens
                            </CheckboxLabel>
                            <HelperText>
                                O assistente envia cada parágrafo da resposta como uma mensagem separada. Desmarcado, a
                                resposta vai inteira em uma única mensagem.
                            </HelperText>
                        </div>
                    )}
                </FormGroup>
            </Modal>
        </>
    );
}
